import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { authService } from '../../services/authService';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import Button from '../../components/UI/Button';

const VerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) {
        setStatus('error');
        setMessage('Invalid verification link. Please check your email for the correct link.');
        return;
      }

      try {
        await authService.verifyEmail({ token });
        setStatus('success');
        setMessage('Your email has been successfully verified! You can now log in to your account.');
      } catch (error: any) {
        setStatus('error');
        setMessage(
          error.response?.data?.error?.message ||
          'Email verification failed. The link may be expired or invalid.'
        );
      }
    };

    verifyEmail();
  }, [token]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-foreground">
            Email Verification
          </h2>
        </div>

        <div className="bg-card py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {status === 'loading' && (
            <div className="text-center">
              <LoadingSpinner size="lg" className="mx-auto" />
              <p className="mt-4 text-sm text-muted-foreground">
                Verifying your email address...
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="text-center">
              <CheckCircleIcon className="mx-auto h-12 w-12 text-success" />
              <h3 className="mt-4 text-lg font-medium text-foreground">
                Email Verified!
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <div className="mt-6">
                <Button
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  variant="primary"
                >
                  Sign In
                </Button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="text-center">
              <XCircleIcon className="mx-auto h-12 w-12 text-destructive" />
              <h3 className="mt-4 text-lg font-medium text-foreground">
                Verification Failed
              </h3>
              <p className="mt-2 text-sm text-muted-foreground">{message}</p>
              <div className="mt-6 space-y-3">
                <Button
                  to="/login"
                  className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-primary-foreground bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                  variant="primary"
                >
                  Back to Sign In
                </Button>
                <Button
                  type="button"
                  variant="outlined"
                  className="w-full"
                  onClick={() => {
                    // TODO: Implement resend verification email
                    toast('Resend verification email functionality will be implemented.');
                  }}
                >
                  Resend Verification Email
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmailPage;